import { DeterministicCheck, LocaleCode, SourceContent, TerminologyConcept } from './types';
import { approvedTerm, forbiddenTerms } from './terminology';

const placeholderPattern = /(\{[a-zA-Z0-9_]+\}|%[sd]|\$\{[a-zA-Z0-9_]+\})/g;
const urlPattern = /https?:\/\/\S+/g;
const cvePattern = /CVE-\d{4}-\d+/g;
const versionPattern = /\b\d+(?:\.\d+)+\b/g;
const ipPattern = /\b\d{1,3}(?:\.\d{1,3}){3}\b/g;
const htmlTagPattern = /<\/?[a-z][^>]*>/gi;

function matches(pattern: RegExp, value: string): string[] {
  return value.match(pattern) ?? [];
}

function sameSet(name: string, sourceText: string, localizedText: string, pattern: RegExp): DeterministicCheck {
  const sourceMatches = matches(pattern, sourceText);
  const targetMatches = matches(pattern, localizedText);
  const missing = sourceMatches.filter((item) => !targetMatches.includes(item));
  return {
    name,
    passed: missing.length === 0,
    blocking: true,
    message: missing.length > 0 ? `Missing preserved token(s): ${missing.join(', ')}` : undefined,
  };
}

export function runDeterministicQA(
  source: SourceContent,
  localizedText: string,
  locale: LocaleCode,
  detectedConcepts: TerminologyConcept[],
): DeterministicCheck[] {
  const checks: DeterministicCheck[] = [
    {
      name: 'empty_output',
      passed: localizedText.trim().length > 0,
      blocking: true,
      message: localizedText.trim().length === 0 ? 'Localized output is empty.' : undefined,
    },
    sameSet('placeholder_integrity', source.sourceText, localizedText, placeholderPattern),
    sameSet('url_preservation', source.sourceText, localizedText, urlPattern),
    sameSet('cve_identifier_preservation', source.sourceText, localizedText, cvePattern),
    sameSet('version_number_preservation', source.sourceText, localizedText, versionPattern),
    sameSet('ip_address_preservation', source.sourceText, localizedText, ipPattern),
    sameSet('html_tag_preservation', source.sourceText, localizedText, htmlTagPattern),
  ];

  const numberCheck = sameSet('number_preservation', source.sourceText, localizedText, /\b\d+\b/g);
  checks.push(numberCheck);

  detectedConcepts.forEach((concept) => {
    const approved = approvedTerm(concept, locale);
    if (approved) {
      const englishLeakAllowed = approved.toLowerCase() === concept.canonicalEnglish.toLowerCase();
      const hasApproved = localizedText.toLowerCase().includes(approved.toLowerCase());
      checks.push({
        name: 'terminology_required',
        passed: hasApproved || englishLeakAllowed,
        blocking: true,
        message: hasApproved || englishLeakAllowed
          ? undefined
          : `Terminology violation: expected ${approved}, received candidate without approved ${concept.canonicalEnglish} term.`,
      });
    }

    forbiddenTerms(concept, locale).forEach((forbidden) => {
      const hasForbidden = localizedText.toLowerCase().includes(forbidden.toLowerCase());
      checks.push({
        name: 'forbidden_terminology_absent',
        passed: !hasForbidden,
        blocking: true,
        message: hasForbidden ? `Terminology violation: expected ${approved ?? concept.canonicalEnglish}, received ${forbidden}` : undefined,
      });
    });
  });

  if (source.maxLength) {
    checks.push({
      name: 'max_length',
      passed: localizedText.length <= Math.ceil(source.maxLength * 1.4),
      blocking: false,
      message: localizedText.length > Math.ceil(source.maxLength * 1.4)
        ? `Text is longer than the expanded max length budget of ${Math.ceil(source.maxLength * 1.4)}.`
        : undefined,
    });
  }

  checks.push({
    name: 'target_language_leakage',
    passed: locale === 'en' || localizedText !== source.sourceText,
    blocking: false,
    message: locale !== 'en' && localizedText === source.sourceText ? 'Candidate equals English source.' : undefined,
  });

  return checks;
}

export function blockingIssues(checks: DeterministicCheck[]): string[] {
  return checks.filter((check) => check.blocking && !check.passed).map((check) => check.message ?? check.name);
}

export function validateSecurityEntities(sourceText: string, output: string): DeterministicCheck[] {
  return [
    sameSet('assistant_cve_preservation', sourceText, output, cvePattern),
    sameSet('assistant_version_preservation', sourceText, output, versionPattern),
    sameSet('assistant_ip_preservation', sourceText, output, ipPattern),
    sameSet('assistant_url_preservation', sourceText, output, urlPattern),
    sameSet('assistant_file_path_preservation', sourceText, output, /\/[a-z0-9_./-]+/gi),
  ];
}
